import { uniq } from 'lodash/fp';

import {
  isCreateVerificationAction,
  isDynamicFlowFieldsReferenceAction,
  WorkflowActionUpdateInquiryNextStepNameV1,
  WorkflowDynamicFlowFieldsReferenceAction,
  WorkflowRunVerificationAction,
  WorkflowStep,
  WorkflowStepAction,
  WorkflowStepActionKey,
  WorkflowStepConditional,
  WorkflowStepParallel,
  WorkflowStepType,
  WorkflowStepWait,
} from 'dashboard/models/workflow-version';

// only workflow step types currently supported in FLE
type FLEWorkflowStep =
  | WorkflowStepAction
  | WorkflowStepConditional
  | WorkflowStepParallel
  | WorkflowStepWait;

/**
 * Returns a unique list of all of the step names that a workflow version
 * transitions to via the update_next_step action step
 * @param workflowSteps
 */
export const workflowNextStepNames = (workflowSteps: WorkflowStep[] | undefined): string[] =>
  uniq(
    getAllWorkflowActionSteps(workflowSteps ?? [])
      .filter(isUpdateInquiryNextStepActionStep)
      .map((workflowStep) => workflowStep.action.params['next-step-name']),
  ).filter(Boolean);

/**
 * Returns a unique list of all of the verification-template-version-ids
 * that a workflow version transitions to via the update_next_step action step
 * @param workflowSteps
 */
export const workflowVtmplvIds = (workflowSteps: WorkflowStep[]): string[] =>
  uniq(
    getAllWorkflowActionSteps(workflowSteps)
      .filter(isRunVerificationActionStep)
      .map((workflowStep) => workflowStep.action.params['verification-template-version-id']),
  ).filter(Boolean);

/**
 * Returns a flattened list of FLE workflow steps.
 * Used when extracting vtmplv and next-step-name references from a workflow version.
 * @param workflowSteps workflow steps-config in default nested format
 */

export const getAllWorkflowActionSteps = (
  workflowSteps: WorkflowStep[] | undefined,
): WorkflowStepAction[] =>
  (workflowSteps ?? [])
    .filter(isFLEWorkflowStepType)
    .flatMap((workflowStep) => {
      switch (workflowStep.type) {
        case WorkflowStepType.Action:
          return workflowStep;
        case WorkflowStepType.Conditional:
        case WorkflowStepType.Parallel:
          return workflowStep.branches.flatMap((branch) => getAllWorkflowActionSteps(branch));
        default:
          return [];
      }
    })
    .flat();

export const getAllWorkflowConditionalSteps = (
  workflowSteps: WorkflowStep[],
): WorkflowStepConditional[] =>
  workflowSteps
    .filter(isFLEWorkflowStepType)
    .flatMap((workflowStep) =>
      workflowStep.type === WorkflowStepType.Conditional
        ? [workflowStep].concat(
            workflowStep.branches.flatMap((branch) => getAllWorkflowConditionalSteps(branch)),
          )
        : [],
    )
    .flat();

export const isFieldsReferenceActionStep = (
  workflowStep: WorkflowStep,
): workflowStep is WorkflowStepAction & { action: WorkflowDynamicFlowFieldsReferenceAction } => {
  return (
    workflowStep.type === WorkflowStepType.Action &&
    isDynamicFlowFieldsReferenceAction(workflowStep.action)
  );
};

/**
 * Type-checker for create/run_verification action step
 * @param workflowStep
 */
export const isRunVerificationActionStep = (
  workflowStep: WorkflowStep,
): workflowStep is WorkflowStepAction & { action: WorkflowRunVerificationAction } => {
  return (
    workflowStep.type === WorkflowStepType.Action && isCreateVerificationAction(workflowStep.action)
  );
};

/**
 * Type-checker for update_inquiry_next_step_name action step
 * @param workflowStep
 */
export const isUpdateInquiryNextStepActionStep = (
  workflowStep: WorkflowStep | undefined,
): workflowStep is WorkflowStepAction & { action: WorkflowActionUpdateInquiryNextStepNameV1 } => {
  return (
    workflowStep?.type === WorkflowStepType.Action &&
    workflowStep?.action?.key === WorkflowStepActionKey.UpdateInquiryNextStepName
  );
};

/**
 * Type-checker for FLE workflow step
 * @param workflowStep
 */
const isFLEWorkflowStepType = (workflowStep: WorkflowStep): workflowStep is FLEWorkflowStep => {
  switch (workflowStep.type) {
    case WorkflowStepType.Action:
    case WorkflowStepType.Conditional:
    case WorkflowStepType.Parallel:
    case WorkflowStepType.Wait:
      return true;
    default:
      return false;
  }
};
